/**
 * Fase 5+6 — Kurs-veiviser steg 3: Lokasjon.
 * Type (fysisk/digital/blended) + Leaflet-kart (Fase 6) ELLER møte-URL.
 */
import { MapPin, Video, Monitor } from 'lucide-react';
import type { CourseWizardData } from '../NewCourseInstanceWizard';
import LocationPicker from './LocationPicker';
import InlineHelpTerm from '../InlineHelpTerm';

export interface CourseLocationStepProps {
  data: CourseWizardData;
  patch: (updates: Partial<CourseWizardData>) => void;
}

const LOCATION_TYPES: Array<{
  value: CourseWizardData['location_type'];
  label: string;
  description: string;
  icon: React.ReactNode;
}> = [
  {
    value: 'physical',
    label: 'Fysisk oppmøte',
    description: 'Kandidatene møter opp på et bestemt sted',
    icon: <MapPin className="w-5 h-5" />,
  },
  {
    value: 'digital',
    label: 'Digital gjennomføring',
    description: 'Møte-URL deles med kandidatene (Teams/Zoom/annet)',
    icon: <Video className="w-5 h-5" />,
  },
  {
    value: 'blended',
    label: 'Blandet (fysisk + digital)',
    description: 'Både fysisk sted OG møte-URL',
    icon: <Monitor className="w-5 h-5" />,
  },
];

export default function CourseLocationStep({ data, patch }: CourseLocationStepProps) {
  const showPhysical = data.location_type === 'physical' || data.location_type === 'blended';
  const showDigital = data.location_type === 'digital' || data.location_type === 'blended';

  return (
    <div className="space-y-5">
      {/* Type-velger */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-2">Lokasjonstype</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {LOCATION_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => patch({ location_type: t.value })}
              className={`text-left p-3 rounded-lg border transition-colors ${
                data.location_type === t.value
                  ? 'bg-kf-blue-50 border-kf-blue-400 ring-1 ring-kf-blue-400'
                  : 'bg-white border-kf-gray-200 hover:bg-kf-gray-50'
              }`}
            >
              <div className="flex items-center gap-2 mb-1 text-kf-blue-600">
                {t.icon}
                <span className="font-medium text-kf-gray-900 text-sm">{t.label}</span>
              </div>
              <p className="text-xs text-kf-gray-600">{t.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Fysisk lokasjon */}
      {showPhysical && (
        <div className="rounded-lg border border-kf-blue-200 p-4 bg-kf-blue-50/40 space-y-3">
          <h4 className="font-semibold text-kf-gray-900 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-kf-blue-600" />
            <InlineHelpTerm
              slug="wizard-location-map"
              label="Fysisk sted"
              route="/courses-and-exams"
            />
          </h4>
          <LocationPicker
            lat={data.location_lat}
            lng={data.location_lng}
            address={data.location}
            onChange={({ lat, lng, address }) => {
              // Prøv å utlede by fra Nominatim-resultatet (siste del før postnummer)
              let city = data.city;
              const parts = address.split(',').map((s) => s.trim());
              const cityPart = parts.find((p) => /^\d{4}\s/.test(p));
              if (cityPart) {
                city = cityPart.replace(/^\d{4}\s/, '').trim();
              } else if (parts.length >= 2) {
                city = parts[parts.length - 2] || city;
              }
              patch({ location_lat: lat, location_lng: lng, location: address, city });
            }}
          />
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">By (justér hvis nødvendig)</label>
            <input
              type="text"
              value={data.city}
              onChange={(e) => patch({ city: e.target.value })}
              placeholder="F.eks: Oslo"
              className="w-64 px-3 py-2 text-sm border border-kf-gray-300 rounded-lg bg-white"
            />
          </div>
        </div>
      )}

      {/* Digital lokasjon */}
      {showDigital && (
        <div className="rounded-lg border border-purple-200 p-4 bg-purple-50/40 space-y-3">
          <h4 className="font-semibold text-kf-gray-900 flex items-center gap-2">
            <Video className="w-4 h-4 text-purple-600" />
            Digital gjennomføring
          </h4>
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">Møte-URL</label>
            <input
              type="url"
              value={data.meeting_url}
              onChange={(e) => patch({ meeting_url: e.target.value })}
              placeholder="https://teams.microsoft.com/… eller https://zoom.us/j/…"
              className="w-full px-3 py-2 text-sm border border-kf-gray-300 rounded-lg bg-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-kf-gray-700 mb-1">Instrukser til kandidater</label>
            <textarea
              value={data.meeting_instructions}
              onChange={(e) => patch({ meeting_instructions: e.target.value })}
              rows={3}
              placeholder="F.eks: Logg inn 10 min før oppstart. Bruk Chrome. Headset anbefales."
              className="w-full px-3 py-2 text-sm border border-kf-gray-300 rounded-lg bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}
